<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
  <p class="text-theme-sm text-gray-500 dark:text-gray-400" aria-live="polite">
    @if (totalCount > 0) {
      {{ totalCount }} producto{{ totalCount !== 1 ? 's' : '' }}@if (searchTerm) {<span> para "{{ searchTerm }}"</span>}
    }
  </p>
  <div class="flex flex-wrap items-center gap-2">
    <div class="relative min-w-0 flex-1 sm:flex-none">
      <svg class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none"
        stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="search" [value]="searchTerm" (input)="onSearch($any($event.target).value)"
        placeholder="Buscar producto" aria-label="Buscar producto por nombre o SKU" title="Busca por nombre o SKU"
        class="iv-input h-10 w-full pl-9 pr-3 sm:w-64" />
    </div>

    <div class="flex items-center gap-2">
      <button type="button" (click)="importFileInput.click()" class="iv-btn iv-btn--ghost inline-flex items-center gap-2"
        [disabled]="isSaving" aria-label="Importar productos desde Excel" title="Importar productos desde Excel">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
        </svg>
        <span class="hidden sm:inline">Importar</span>
      </button>
      <input #importFileInput type="file" accept=".xlsx,.xls" (change)="onImportFile($event)" class="hidden" />
      <button type="button" (click)="downloadTemplate()" class="iv-btn iv-btn--ghost inline-flex items-center gap-2"
        aria-label="Descargar plantilla de Excel" title="Descargar plantilla de Excel">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        <span class="hidden sm:inline">Plantilla</span>
      </button>
      <button type="button" (click)="onCreateProduct()" class="iv-btn iv-btn--primary inline-flex shrink-0 items-center gap-2"
        aria-label="Crear producto">
        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span class="hidden sm:inline">Crear producto</span>
      </button>
    </div>
  </div>
</div>

<!-- Skeleton -->
@if (loading && products.length === 0) {
  <div class="iv-list" role="status" aria-label="Cargando productos">
    @for (item of skeletonRows; track $index) {
      <div class="iv-row animate-pulse">
        <div style="grid-area: info">
          <div class="mb-2 h-4 w-44 max-w-full rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800/70"></div>
        </div>
        <div class="h-3 w-28 rounded bg-gray-100 dark:bg-gray-800/70" style="grid-area: stock"></div>
        <div class="ml-auto h-4 w-20 rounded bg-gray-200 dark:bg-gray-800" style="grid-area: price"></div>
      </div>
    }
  </div>
}

<!-- Empty state -->
@if (!loading && products.length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    @if (searchTerm) {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Ningún producto coincide con "{{ searchTerm }}"</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Revisa la ortografía o busca por el código SKU.
      </p>
      <button type="button" (click)="onSearch(''); searchTerm = ''" class="iv-btn iv-btn--ghost">Limpiar búsqueda</button>
    } @else {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Aún no tienes productos</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Registra lo que vendes en el local, con su costo y existencias, o importa tu lista desde Excel.
      </p>
      <div class="flex flex-wrap justify-center gap-2">
        <button type="button" (click)="onCreateProduct()" class="iv-btn iv-btn--primary inline-flex items-center gap-2">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Crear tu primer producto
        </button>
        <button type="button" (click)="importFileInput.click()" class="iv-btn iv-btn--ghost">Importar desde Excel</button>
      </div>
    }
  </div>
}

<!-- Product list -->
@if (products.length > 0) {
  <div class="iv-list" [class.opacity-60]="loading">
    <div class="iv-row iv-row--head" aria-hidden="true">
      <span style="grid-area: info">Producto</span>
      <span style="grid-area: stock">Existencias</span>
      <span class="text-right" style="grid-area: price">Precio de venta</span>
    </div>

    <ul>
      @for (product of products; track product.productId) {
        @let state = stockState(product);
        <li class="iv-row" [class.iv-row--inactive]="!product.isActive">
          <div class="min-w-0" style="grid-area: info">
            <h3 class="iv-name truncate">{{ product.name }}</h3>
            <p class="iv-meta">
              @if (product.category) {
                <span class="iv-tag">{{ product.category }}</span>
              }
              @if (product.sku) {
                <span class="iv-sku">{{ product.sku }}</span>
              }
              @if (!product.isActive) {
                <span class="iv-tag iv-tag--off">Inactivo</span>
              }
            </p>
          </div>

          <!-- Stock shelf -->
          <div class="iv-stock" [attr.data-state]="state" style="grid-area: stock">
            <span class="iv-shelf" aria-hidden="true">
              @for (seg of shelfSlots; track $index) {
                <span class="iv-shelf__slot" [class.iv-shelf__slot--on]="$index < shelfFill(product)"></span>
              }
            </span>
            <span class="iv-stock__label">
              @switch (state) {
                @case ('out') { Agotado }
                @case ('low') { Quedan {{ product.stock }} }
                @default { {{ product.stock }} unidades }
              }
            </span>
          </div>

          <div class="iv-price" style="grid-area: price">
            <p class="iv-price__value">{{ formatPrice(product.price) }}</p>
            @if (marginPercent(product) !== null) {
              <p class="iv-price__margin" [class.iv-price__margin--neg]="marginPercent(product)! < 0">
                Costo {{ formatPrice(product.cost) }}, margen {{ marginPercent(product)! | number:'1.0-0' }} %
              </p>
            }
          </div>

          <div class="flex items-center justify-end" style="grid-area: actions">
            <button type="button" class="iv-icon-btn" (click)="onEditProduct(product.productId)"
              [attr.aria-label]="'Editar ' + product.name" title="Editar">
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
            </button>
          </div>
        </li>
      }
    </ul>
  </div>

  <!-- Pagination -->
  @if (totalPages > 1) {
    <nav class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row" aria-label="Paginación">
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        {{ (page - 1) * pageSize + 1 }}–{{ page * pageSize > totalCount ? totalCount : page * pageSize }} de {{ totalCount }}
      </p>
      <div class="flex items-center gap-1">
        <button type="button" (click)="onPageChange(page - 1)" [disabled]="page <= 1" class="iv-page" aria-label="Página anterior">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        @for (p of [].constructor(totalPages); track $index) {
          @if ($index + 1 === page || $index + 1 === 1 || $index + 1 === totalPages || ($index + 1 >= page - 1 && $index + 1 <= page + 1)) {
            <button type="button" (click)="onPageChange($index + 1)" class="iv-page"
              [attr.aria-current]="($index + 1) === page ? 'page' : null">
              {{ $index + 1 }}
            </button>
          } @else if ($index + 1 === page - 2 || $index + 1 === page + 2) {
            <span class="inline-flex size-9 items-center justify-center text-sm text-gray-400" aria-hidden="true">…</span>
          }
        }
        <button type="button" (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="iv-page" aria-label="Página siguiente">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </nav>
  }
}

<app-modal [open]="open" [title]="modalTitle" [size]="'md'" [bodyComponent]="ProductAddForm" [closeOnBackdrop]="false"
  [bodyInputs]="modalInputs" (confirm)="onModalConfirm($event)" (close)="open = false" [isSaving]="isSaving"
  [confirmButtonText]="confirmButtonText">
</app-modal>
